/**
 * Final / hero-eliminated overlay. Shows the tournament standings (if
 * finished) or a simple "당신 탈락" message (if hero busted mid-game),
 * plus a "새 토너먼트 시작" button that restarts with a fresh seed.
 * Rendered inline at the bottom of /play — never a modal.
 */

import { computeFinalStandings } from '@/engine/tournament';
import type { PlayerId } from '@/engine/types';
import { useGameStore } from '@/state/gameStore';

export type FinalSummaryProps = {
  heroId: PlayerId;
  mode: 'finished' | 'eliminated';
  onNewTournament: () => void;
  characterLabelFor: (playerId: PlayerId) => string;
};

export function FinalSummary({
  heroId,
  mode,
  onNewTournament,
  characterLabelFor,
}: FinalSummaryProps) {
  const tournament = useGameStore((s) => s.tournament);

  let standings: Array<{ rank: number; playerId: PlayerId; prize: number }> = [];
  if (tournament && mode === 'finished') {
    try {
      standings = [...computeFinalStandings(tournament)];
    } catch {
      standings = [];
    }
  }

  const heroRank = standings.find((r) => r.playerId === heroId)?.rank ?? null;
  const heroPrize = standings.find((r) => r.playerId === heroId)?.prize ?? 0;

  const headline =
    mode === 'finished'
      ? heroRank === 1
        ? '🏆 1위! 축하합니다'
        : heroRank && heroRank <= 3
          ? `🥉 ${heroRank}위 · 상금 ${heroPrize}`
          : `토너먼트 종료 · ${heroRank ?? '–'}위`
      : '당신 탈락';

  return (
    <section
      className="flex flex-col gap-3 px-4 py-4 bg-bg-elev border-t border-border"
      data-testid="final-summary"
      data-mode={mode}
    >
      <h2 className="text-sm font-semibold text-center">{headline}</h2>

      {mode === 'finished' && standings.length > 0 ? (
        <ol className="flex flex-col gap-1" data-testid="final-standings">
          {standings.map((row) => (
            <li
              key={row.playerId}
              className={`flex items-center justify-between text-xs px-3 py-1.5 rounded-md font-mono ${
                row.playerId === heroId
                  ? 'bg-accent/15 border border-accent text-fg'
                  : 'bg-bg border border-border text-muted'
              }`}
            >
              <span className="tabular-nums">
                {row.rank}.{' '}
                <span className="text-fg">
                  {row.playerId === heroId ? '당신' : characterLabelFor(row.playerId)}
                </span>
              </span>
              <span className="tabular-nums">{row.prize > 0 ? `+${row.prize}` : '—'}</span>
            </li>
          ))}
        </ol>
      ) : null}

      {mode === 'eliminated' ? (
        <p className="text-xs text-muted text-center">
          스택이 0으로 떨어져 토너먼트에서 탈락했습니다.
          <br />새 토너먼트를 시작해 다시 도전할 수 있습니다.
        </p>
      ) : null}

      <button
        type="button"
        onClick={onNewTournament}
        className="rounded-md bg-accent text-bg text-sm font-semibold py-2"
        data-testid="final-new-tournament"
      >
        새 토너먼트 시작 →
      </button>
    </section>
  );
}
